// RESONATE_MOBILE_STICKY_CTA_V1 (P0-3)
// Full-width thumb-zone sticky CTA pinned to the bottom of the mobile viewport.
// Hidden on md+ (desktop uses the pill StickyBar instead).
// Persists dismissal for the session so we don't re-annoy the user after they
// consciously closed it.
import { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { buildSignupUrl } from "@/lib/constants";
import { trackEvent } from "@/lib/analytics";
import { getAdVariantFromUrl } from "@/lib/adVariants";

const DISMISS_KEY = "resonate_mobile_cta_dismissed_v1";

export const MobileStickyCta = () => {
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);

  // Restore dismissal from sessionStorage on mount.
  useEffect(() => {
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === "1") setDismissed(true);
    } catch { /* private mode */ }
  }, []);

  // Which variant is this visit? Used to route the CTA to the right utm_campaign.
  const variant = useMemo(
    () => getAdVariantFromUrl(location.pathname.startsWith("/agencies") ? "agency" : "founder"),
    [location.pathname, location.search, location.hash]
  );

  if (dismissed) return null;

  const href = buildSignupUrl(variant ? `mobile_sticky_${variant.slug}` : "mobile_sticky");

  const onDismiss = () => {
    trackEvent("mobile_sticky_dismiss");
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* ok */ }
    setDismissed(true);
  };

  return (
    <div
      data-testid="mobile-sticky-cta"
      className="md:hidden fixed left-0 right-0 bottom-0 z-50 bg-white border-t border-[rgba(38,31,73,0.12)] shadow-[0_-8px_24px_-8px_rgba(38,31,73,0.15)]"
      style={{
        // Respect iOS safe-area (iPhone notch home indicator).
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 8px)",
      }}
    >
      <div className="relative px-4 pt-3 pb-1">
        <button
          type="button"
          aria-label="Dismiss"
          data-testid="mobile-sticky-dismiss"
          onClick={onDismiss}
          className="absolute -top-1 right-2 w-8 h-8 grid place-items-center text-[rgba(17,24,39,0.45)] hover:text-[#261F49] active:text-[#261F49]"
        >
          <X className="w-4 h-4" />
        </button>

        <a
          href={href}
          onClick={() => trackEvent("cta_click", { location: "mobile_sticky", variant: variant?.slug || null })}
          data-testid="mobile-sticky-cta-btn"
          className="w-full flex items-center justify-center gap-2 bg-[#261F49] hover:bg-[#342255] active:bg-[#342255] text-white rounded-xl font-semibold text-[16px] transition-colors"
          style={{ minHeight: 56 }}
        >
          Start for free
          <ArrowRight className="w-4 h-4" />
        </a>

        <p
          data-testid="mobile-sticky-microcopy"
          className="text-center text-[12px] text-[rgba(17,24,39,0.6)] mt-1.5 leading-tight"
        >
          30-day money-back guarantee · $0 today, cancel in two clicks.
        </p>
      </div>
    </div>
  );
};
